import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { ProfileNav } from '../components/layout/ProfileNav';
import { updatePassword } from '../modules/platform/api/auth';
import { handleApiError, showErrorToast, showSuccessToast } from '../utils/errorHandler';
import { validatePassword } from '../utils/validation';

interface ChangePasswordFormData {
  current_password: string;
  new_password: string;
  confirm_password: string;
}

export const ProfilePage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ChangePasswordFormData>();

  const newPassword = watch('new_password');

  const onSubmitPassword = async (data: ChangePasswordFormData) => {
    setPasswordError(null);
    if (data.new_password !== data.confirm_password) {
      showErrorToast(new Error('Passwords do not match'));
      return;
    }
    setIsLoading(true);
    try {
      await updatePassword({
        current_password: data.current_password,
        new_password: data.new_password,
      });
      showSuccessToast('Password updated successfully');
      reset({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      const message = handleApiError(error);
      setPasswordError(message);
      showErrorToast(error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-6xl">
        <div className="grid gap-6 lg:grid-cols-[200px_1fr]">
          <aside className="space-y-6 lg:sticky lg:top-6 h-fit">
            <Card padding="md">
              <h2 className="text-sm font-semibold text-app-text dark:text-white uppercase tracking-wider mb-3">
                Profile
              </h2>
              <ProfileNav />
            </Card>
          </aside>
          <main className="space-y-6">
            <h1 className="text-xl font-semibold text-app-text dark:text-white">Overview</h1>
            <Card padding="lg">
              <p className="text-app-text-muted dark:text-gray-400">
                Account overview and profile settings. Use the links on the left to switch sections.
              </p>
            </Card>

            <Card padding="lg">
              <CardHeader>
                <CardTitle className="text-lg">Change password</CardTitle>
                <p className="text-sm text-app-text-muted dark:text-gray-400 mt-1">
                  Update your account password. You will stay signed in.
                </p>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit(onSubmitPassword)} className="space-y-4 max-w-md">
                  <div>
                    <label className="block text-sm font-medium text-app-text-muted dark:text-gray-300 mb-1">
                      Current password
                    </label>
                    <div className="relative">
                      <Input
                        type={showCurrentPassword ? 'text' : 'password'}
                        {...register('current_password', {
                          required: 'Current password is required',
                          onChange: () => setPasswordError(null),
                        })}
                        error={errors.current_password?.message}
                        placeholder="Enter your current password"
                        autoComplete="current-password"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword((prev) => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        aria-label={showCurrentPassword ? 'Hide password' : 'Show password'}
                      >
                        {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    {passwordError && (
                      <p className="mt-1 text-sm text-red-600 dark:text-red-400" role="alert">
                        {passwordError}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-app-text-muted dark:text-gray-300 mb-1">
                      New password
                    </label>
                    <div className="relative">
                      <Input
                        type={showNewPassword ? 'text' : 'password'}
                        {...register('new_password', {
                          required: 'New password is required',
                          validate: (value) => {
                            const result = validatePassword(value);
                            return result.valid || result.message;
                          },
                        })}
                        error={errors.new_password?.message}
                        placeholder="Create a strong password"
                        helperText="At least 8 characters with uppercase, lowercase, and number"
                        autoComplete="new-password"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword((prev) => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        aria-label={showNewPassword ? 'Hide password' : 'Show password'}
                      >
                        {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-app-text-muted dark:text-gray-300 mb-1">
                      Confirm new password
                    </label>
                    <div className="relative">
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        {...register('confirm_password', {
                          required: 'Please confirm your new password',
                          validate: (value) => value === newPassword || 'Passwords do not match',
                        })}
                        error={errors.confirm_password?.message}
                        placeholder="Confirm your new password"
                        autoComplete="new-password"
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                      >
                        {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" isLoading={isLoading}>
                    Update password
                  </Button>
                </form>
              </CardContent>
            </Card>
          </main>
        </div>
      </div>
    </Layout>
  );
};
